import { useCallback, useEffect, useRef, useState } from 'react';
import MI from '../../endpoints/market-intelligence';

// Hook for invoking a Plunk agent and polling for completion (MI spec §20).
//
// Usage:
//   const ai = useAgentRun();
//   ai.start({ agent: 'market_intelligence_orchestrator',
//              locationId, range, comparisonType });
//
// Exposes: { running, result, error, startedAt, finishedAt, start, reset }
// where `result` is the raw agent-run payload after completion.
//
// Cancellation: calling start() again (or unmounting) aborts the previous
// poll loop. reset() clears state without starting a new run.

const POLL_INTERVAL_MS = 3000;
const TERMINAL_STATES = new Set(['completed', 'succeeded', 'failed', 'error', 'cancelled']);
function isTerminal(run) {
  if (!run) return false;
  const status = String(run.status || run.Status || run.state || run.State || '').toLowerCase();
  return TERMINAL_STATES.has(status);
}
function isFailure(run) {
  const status = String(run && (run.status || run.Status || run.state || run.State) || '').toLowerCase();
  return status === 'failed' || status === 'error';
}
export default function useAgentRun() {
  const [state, setState] = useState({
    running: false,
    result: null,
    error: null,
    startedAt: null,
    finishedAt: null,
    runId: null
  });
  const cancelRef = useRef({
    cancelled: false
  });
  useEffect(() => () => {
    cancelRef.current.cancelled = true;
  }, []);
  const reset = useCallback(() => {
    cancelRef.current.cancelled = true;
    cancelRef.current = {
      cancelled: false
    };
    setState({
      running: false,
      result: null,
      error: null,
      startedAt: null,
      finishedAt: null
    });
  }, []);
  const start = useCallback(async params => {
    // Abort any in-flight run.
    cancelRef.current.cancelled = true;
    const token = {
      cancelled: false
    };
    cancelRef.current = token;
    setState({
      running: true,
      result: null,
      error: null,
      startedAt: Date.now(),
      finishedAt: null
    });
    try {
      const launch = await MI.runAgent(params);
      if (token.cancelled) return;
      const runId = launch.runId || launch.RunId || launch.id || launch.Id;
      if (!runId) {
        throw new Error('Agent run did not return a runId');
      }

      // If the backend returned a terminal state immediately, use it.
      if (isTerminal(launch) && (launch.runResult || launch.result || launch.messages || launch.output)) {
        setState({
          running: false,
          result: launch,
          error: isFailure(launch) ? launch.error || 'Agent run failed' : null,
          startedAt: Date.now(),
          finishedAt: Date.now(),
          runId
        });
        return;
      }

      // Otherwise poll.
      while (!token.cancelled) {
        await new Promise(r => setTimeout(r, POLL_INTERVAL_MS));
        if (token.cancelled) return;
        const run = await MI.pollAgentRun(runId);
        if (token.cancelled) return;
        if (isTerminal(run)) {
          setState({
            running: false,
            result: run,
            error: isFailure(run) ? run.error || run.Error || 'Agent run failed' : null,
            startedAt: Date.now() - POLL_INTERVAL_MS,
            finishedAt: Date.now(),
            runId
          });
          return;
        }
      }
    } catch (err) {
      if (token.cancelled) return;
      setState({
        running: false,
        result: null,
        error: err.message || String(err),
        startedAt: null,
        finishedAt: Date.now()
      });
    }
  }, []);

  // Load a previously-completed run by id (no new run launched) — used to
  // rehydrate the user's last analysis for a market. Silent no-op if the run
  // can't be fetched or isn't a usable completed result; never flips
  // `running` (so it doesn't look like a fresh analysis is in progress).
  const loadExisting = useCallback(async runId => {
    if (!runId) return;
    try {
      const run = await MI.pollAgentRun(runId);
      if (cancelRef.current.cancelled) return;
      if (isTerminal(run) && !isFailure(run) && (run.runResult || run.RunResult || run.result || run.fullReport || run.FullReport || run.messages || run.output)) {
        setState(s => ({
          ...s,
          result: run,
          error: null,
          finishedAt: Date.now(),
          runId
        }));
      }
    } catch (e) {/* ignore — nothing to rehydrate */}
  }, []);
  return {
    ...state,
    start,
    reset,
    loadExisting
  };
}

// --- Result parsing ----------------------------------------------------------

// Extract the narrative summary from an agent run. Per the spec, pick the
// longest assistant message OR one containing "Executive Summary".
export function extractSummary(run) {
  if (!run) return null;
  // The Plunk agent API returns the narrative in `runResult`.
  if (typeof run.runResult === 'string') return run.runResult;
  if (typeof run.RunResult === 'string') return run.RunResult;
  if (typeof run.summary === 'string') return run.summary;
  if (typeof run.Summary === 'string') return run.Summary;
  if (typeof run.output === 'string') return run.output;
  const messages = run.messages || run.Messages || run.result?.messages || [];
  if (!Array.isArray(messages) || messages.length === 0) return null;
  const assistant = messages.filter(m => {
    const role = String(m.role || m.Role || '').toLowerCase();
    return role === 'assistant' || role === 'ai';
  });
  if (assistant.length === 0) return null;
  const marked = assistant.find(m => {
    const text = String(m.content || m.Content || m.text || m.Text || '');
    return /executive summary/i.test(text);
  });
  if (marked) return String(marked.content || marked.Content || marked.text || marked.Text || '');
  const longest = assistant.reduce((best, m) => {
    const text = String(m.content || m.Content || m.text || m.Text || '');
    return text.length > (best?.length || 0) ? text : best;
  }, '');
  return longest || null;
}

// Extract structured diagnostic insights from a diagnostics agent run.
// Falls back to parsing the narrative summary if no structured array is
// present — we look for bullet lines prefixed with [critical]/[warning]/[info].
export function extractDiagnostics(run) {
  if (!run) return [];
  const direct = run.diagnostics || run.Diagnostics || run.result?.diagnostics;
  if (Array.isArray(direct)) return direct;
  if (run.runResult && typeof run.runResult === 'object' && Array.isArray(run.runResult.diagnostics)) {
    return run.runResult.diagnostics;
  }
  const summary = extractSummary(run);
  if (!summary) return [];
  const out = [];
  const lines = String(summary).split('\n');
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) continue;
    const m = /^\s*(?:[-*]\s*)?\[?(critical|warning|info|opportunity)\]?\s*[:-]?\s*(.*)$/i.exec(line);
    if (!m) continue;
    const sev = m[1].toLowerCase() === 'opportunity' ? 'info' : m[1].toLowerCase();
    out.push({
      Severity: sev,
      Finding: m[2],
      Recommendation: ''
    });
  }
  return out;
}